import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Trash2, Download, Eye, AlertCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const consentStorage = {
  save: (email, consentData) => {
    const consents = JSON.parse(localStorage.getItem('privacy_consents') || '{}');
    consents[email] = {
      ...consentData,
      timestamp: new Date().toISOString(),
      ip: 'client-side'
    };
    localStorage.setItem('privacy_consents', JSON.stringify(consents));
  },
  get: (email) => {
    const consents = JSON.parse(localStorage.getItem('privacy_consents') || '{}');
    return consents[email];
  },
  delete: (email) => {
    const consents = JSON.parse(localStorage.getItem('privacy_consents') || '{}');
    delete consents[email];
    localStorage.setItem('privacy_consents', JSON.stringify(consents));
  },
  getAll: () => {
    return JSON.parse(localStorage.getItem('privacy_consents') || '{}');
  }
};

export const ConsentCheckbox = ({ onConsentChange, required = true }) => {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    onConsentChange?.(isChecked);
  }, [isChecked, onConsentChange]);

  return (
    <div className="space-y-2">
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
          className="mt-0.5 w-4 h-4 text-primary bg-fg/5 border-fg/10 rounded focus:ring-primary"
          required={required}
        />
        <span className="text-sm text-fg/60">
          {t('consentText')}
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="ml-1 text-primary hover:underline"
          >
            {t('consentDetails')}
          </button>
        </span>
      </label>

      {showDetails && (
        <div className="ml-7 p-3 bg-fg/5 rounded-lg text-xs text-fg/60 space-y-1">
          <p> {t('consentPoint1')}</p>
          <p> {t('consentPoint2')}</p>
          <p> {t('consentPoint3')}</p>
          <p> {t('consentPoint4')}</p>
        </div>
      )}
    </div>
  );
};

export const DataManagement = ({ userEmail, onClose }) => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (userEmail) {
      const data = consentStorage.get(userEmail);
      setUserData(data);
    }
  }, [userEmail]);

  const handleExportData = () => {
    const exportData = {
      email: userEmail,
      consents: userData,
      requests: JSON.parse(localStorage.getItem('form_requests') || '{}')[userEmail] || [],
      exportDate: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `user-data-${userEmail}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setMessage(t('dataExported'));
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteData = () => {
    if (window.confirm(t('confirmDelete'))) {
      consentStorage.delete(userEmail);
      const requests = JSON.parse(localStorage.getItem('form_requests') || '{}');
      delete requests[userEmail];
      localStorage.setItem('form_requests', JSON.stringify(requests));
      
      setMessage(t('dataDeleted'));
      setTimeout(() => {
        setMessage('');
        onClose?.();
      }, 2000);
    }
  };

  if (!userEmail) {
    return (
      <div className="p-4 text-center text-fg/50">
        {t('enterEmailToManage')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {message && (
        <div className="p-3 bg-primary/10 text-primary rounded-lg text-sm">
          {message}
        </div>
      )}
      
      <div className="p-4 bg-fg/5 rounded-lg">
        <p className="text-sm font-medium mb-2">{t('yourDataInfo')}</p>
        <p className="text-xs text-fg/50">
          {t('consentGiven')}: {userData?.timestamp ? new Date(userData.timestamp).toLocaleString() : t('noConsent')}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={handleExportData}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
        >
          <Download size={18} />
          {t('exportMyData')}
        </button>
        
        <button
          onClick={handleDeleteData}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition"
        >
          <Trash2 size={18} />
          {t('deleteMyData')}
        </button>
      </div>

      <p className="text-xs text-fg/50 text-center">
        {t('dataRetentionNote')}
      </p>
    </div>
  );
};

export const PrivacyPolicyLink = ({ children, className }) => {
  const { t } = useTranslation();
  
  return (
    <Link 
      to="/privacy-policy" 
      className={className || "flex items-center gap-1 text-xs text-primary hover:underline"}
    >
      <Shield size={12} />
      {children || t('readPrivacyPolicy')}
    </Link>
  );
};

export const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-fg/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-white dark:bg-fg/10 rounded-2xl shadow-2xl">
        <div className="sticky top-0 flex justify-between items-center p-6 border-b bg-gradient-to-r from-primary to-secondary">
          <h2 className="text-2xl font-bold text-white">{t('privacyPolicy')}</h2>
          <button onClick={onClose} className="text-white hover:opacity-80">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-fg/80">
          <p className="text-center text-fg/50">
            {t('viewFullPolicy')}{' '}
            <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline" onClick={onClose}>
              {t('clickHere')}
            </Link>
          </p>
          <div className="pt-4 text-sm text-fg/50 border-t text-center">
            <p>{t('policyLastUpdated')}: 15.05.2026</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const usePrivacyConsent = () => {
  const [hasConsent, setHasConsent] = useState(false);
  const [consentError, setConsentError] = useState('');

  const validateConsent = (isChecked) => {
    if (!isChecked) {
      setConsentError('Необходимо дать согласие на обработку персональных данных');
      return false;
    }
    setConsentError('');
    return true;
  };

  const saveConsent = (email, consentData) => {
    consentStorage.save(email, consentData);
  };

  return {
    hasConsent,
    setHasConsent,
    consentError,
    validateConsent,
    saveConsent
  };
};

export default { ConsentCheckbox, DataManagement, PrivacyPolicyLink, PrivacyPolicyModal, usePrivacyConsent };